'use client'

import type { EditorProps as MonacoEditorProps, OnChange, OnMount } from '@monaco-editor/react'
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material'
import { FeatureFlagIdCopilotCelEditor } from 'c1gen/features'
import { monoFont, spacing } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import {
    makeCELCompletionProvider,
    makeCELHoverProvider,
    makeCELSignatureHelpProvider,
    parseExpressionContext,
} from 'components/core/editor/cel'
import { CELSchema, useCELSchema } from 'components/core/editor/CELContext'
import { CELTooltip } from 'components/core/editor/CELTooltip'
import { editorBackgroundColor, editorTheme } from 'components/core/editor/editorTheme'
import { initMonacoWorkers } from 'components/core/editor/monacoWorkers'
import { VariableBrowserContent } from 'components/core/editor/VariableBrowser'
import { C1Icon } from 'components/core/icons/C1Icon'
import { HasFeature } from 'components/no-ui/capability'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { useClientRPC } from 'lib/rpcts'
import { Braces, Sparkles } from 'lucide-react'
import type monaco from 'monaco-editor'
import type { MarkerSeverity } from 'monaco-editor'
import dynamic, { DynamicOptionsLoadingProps } from 'next/dynamic'
import { AppSearchClient } from 'pbts/c1/api/app/v1/app.client'
import {
    EditorValidateRequest as ConnectorEditorValidateRequest,
} from 'pbts/c1/api/app/v1/connector'
import {
    EditorMarkerSeverity,
    EditorServiceValidateCELRequest as CelEditorValidateRequest,
    EditorValidationEnv,
    FormFieldCELContext,
    WorkflowCELContext,
} from 'pbts/c1/api/editor/v1/editor'
import { UserSearchClient } from 'pbts/c1/api/user/v1/user.client'
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { validateHTTPConnectorConfigApi } from 'redux_toolkit/connectors'
import { editorService } from 'redux_toolkit/editor'

type EditorOptions = Omit<monaco.editor.IStandaloneEditorConstructionOptions, 'model'>

type EditorProps = Omit<MonacoEditorProps, 'options' | 'overrideServices'> & {
    options?: EditorOptions
    minHeight?: string
    maxHeight?: string
    enableFetchValidation?: boolean
    hasToolTip?: boolean
    validationEnv?: EditorValidationEnv
    /** Workflow context for workflow-related CEL environments */
    workflowContext?: WorkflowCELContext
    /** Form field context for form field CEL environments */
    formFieldContext?: FormFieldCELContext
    /** Whether to show the variable browser button */
    showVariableBrowser?: boolean
    /** Controlled state for copilot active */
    copilotActive?: boolean
    /** Callback when copilot toggle is clicked */
    onCopilotToggle?: (active: boolean) => void
}

const celLanguage: monaco.languages.ILanguageExtensionPoint & {
    language: monaco.languages.IMonarchLanguage,
    languageConfiguration: monaco.languages.LanguageConfiguration,
} = {
    id: 'cel',
    extensions: ['.cel'],
    aliases: ['CEL'],
    mimetypes: ['text/cel'],
    language: {
        keywords: ['in', 'as', 'break', 'const', 'continue', 'else', 'for', 'function', 'if', 'import', 'let', 'loop', 'package', 'namespace', 'return', 'var', 'void', 'while'],
        typeKeywords: ['boolean', 'double', 'byte', 'int', 'short', 'char', 'void', 'long', 'float'],
        operators: [
            '.',
            '-',
            '!',
            '*',
            '/',
            '%',
            '+',
            '==',
            '!=',
            '<',
            '>',
            '<=',
            '>=',
            'in',
            '&&',
            '||',
            // '?:',
        ],
        symbols: /[=><!~?:&|+\-*/^%]+/,
        boolLiteral: ['true', 'false'],
        nullLiteral: ['null'],
        tokenizer: {
            root: [
                [/[a-z_$][\w$]*/, {
                    cases: {
                        '@typeKeywords': 'keyword',
                        '@keywords': 'keyword',
                        '@default': 'identifier',
                        '@boolLiteral': 'keyword',
                        '@nullLiteral': 'keyword',
                    },
                }],

                { include: '@whitespace' },
                [
                    /@symbols/,
                    {
                        cases: {
                            '@operators': 'delimiter',
                            '@default': '',
                        },
                    },
                ],

                // strings
                [/"([^"\\]|\\.)*$/, 'string.invalid'], // non-teminated string
                [/'([^'\\]|\\.)*$/, 'string.invalid'], // non-teminated string
                [/"/, 'string', '@string_double'],
                [/'/, 'string', '@string_single'],
            ],
            comment: [
                [/[/*]/, 'comment'],
            ],

            whitespace: [
                [/[ \t\r\n]+/, ''],
                [/\/\/.*$/, 'comment'],
            ],
            string_double: [
                [/[^\\"]+/, 'string'],
                [/"/, 'string', '@pop'],
            ],
            string_single: [
                [/[^\\']+/, 'string'],
                [/'/, 'string', '@pop'],
            ],
        },
        brackets: [
            { open: '{', close: '}', token: 'delimiter.curly' },
            { open: '(', close: ')', token: 'delimiter.parenthesis' },
            { open: '[', close: ']', token: 'delimiter.square' },
        ],
    },
    languageConfiguration: {
        comments: {
            // CEL has no multiline comments
            lineComment: '//',
        },
        // symbols used as brackets
        brackets: [
            ['{', '}'],
            ['[', ']'],
            ['(', ')'],
        ],
        // symbols that are auto closed when typing
        autoClosingPairs: [
            { open: '{', close: '}' },
            { open: '[', close: ']' },
            { open: '(', close: ')' },
            { open: '"', close: '"', notIn: ['string'] },
            { open: "'", close: "'", notIn: ['string', 'comment'] },
        ],
        // symbols that can be used to surround a selection
        surroundingPairs: [
            { open: '{', close: '}' },
            { open: '[', close: ']' },
            { open: '(', close: ')' },
            { open: '"', close: '"' },
            { open: "'", close: "'" },
        ],
    },
}

export const Loading = (_loadingProps: DynamicOptionsLoadingProps): React.ReactElement => <Box textAlign="center">
    <C1CircularProgress sx={{ color: 'grey.600' }} />
    <br />
    <Typography color="grey.600"><FormattedMessage id="loading_items" defaultMessage="Loading..." /></Typography>
</Box>

const defaultEditorOptions: EditorOptions = {
    minimap: { enabled: false },
    wordWrap: 'on',
    lineNumbers: 'on',
    automaticLayout: true, // Auto-resize when container changes
    contextmenu: false,
    links: false,
    folding: false,
    overviewRulerBorder: false,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    scrollBeyondLastLine: false,
    lineNumbersMinChars: 3,
    renderLineHighlight: 'none',
    fontFamily: monoFont.style.fontFamily,
    fontSize: 14,
    occurrencesHighlight: 'singleFile',
    'semanticHighlighting.enabled': false, // enable this once we make a semantic tokens provider
    fixedOverflowWidgets: true, // Allow hover widgets to overflow the editor container
    wordBasedSuggestions: 'off', // Disable to avoid duplicates with our schema-based completions
    tabCompletion: 'on',
}

// Module-level state for provider registration
let celCompletionProviderDisposable: { dispose: () => void } | null = null

// Module-level schema registry (keyed by model URI) for autocomplete
const schemaByModelUri = new Map<string, CELSchema>()

// Guard against re-initialization on hot reload (improves dev server performance)
let monacoInitialized = false
let yamlSchemaConfigured = false
let celProvidersRegistered = false

// Lazy load the HTTP schema only when needed (it's large)
const getHttpSchema = async (): Promise<unknown> => {
    // http.json is copied from https://github.com/ConductorOne/baton-http/blob/main/schema.json
    const httpModule = await import('./http.json')
    return httpModule.default
}

export const DynamicEditor = dynamic(async () => {
    const [{ Editor, loader }, monacoModule, { configureMonacoYaml }] = await Promise.all([
        import('@monaco-editor/react'),
        import('monaco-editor'),
        import('monaco-yaml'),
    ])

    // Only initialize Monaco once (prevents re-registration on hot reload)
    if (!monacoInitialized) {
        monacoInitialized = true

        initMonacoWorkers()
        loader.config({ monaco: monacoModule })

        // Theme
        monacoModule.editor.defineTheme('c1', editorTheme)
        monacoModule.editor.setTheme('c1')

        // CEL Language support
        monacoModule.languages.register(celLanguage)
        monacoModule.languages.setMonarchTokensProvider(celLanguage.id, celLanguage.language)
        monacoModule.languages.setLanguageConfiguration(celLanguage.id, celLanguage.languageConfiguration)
    }

    // Lazy configure YAML schema only once and only when needed
    const configureYamlSchemaOnce = async (): Promise<void> => {
        if (yamlSchemaConfigured) return
        yamlSchemaConfigured = true
        const httpSchema = await getHttpSchema()
        configureMonacoYaml(monacoModule, {
            schemas: [
                {
                    fileMatch: ['*'],
                    uri: 'https://github.com/conductorone/baton-http',
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    schema: httpSchema as any,
                },
            ],
        })
    }

    function toSeverity(severity: EditorMarkerSeverity): MarkerSeverity {
        switch (severity) {
            case EditorMarkerSeverity.ERROR:
                return monacoModule.MarkerSeverity.Error
            case EditorMarkerSeverity.WARNING:
                return monacoModule.MarkerSeverity.Warning
            case EditorMarkerSeverity.INFO:
                return monacoModule.MarkerSeverity.Info
            case EditorMarkerSeverity.HINT:
                return monacoModule.MarkerSeverity.Hint
            case EditorMarkerSeverity.UNKNOWN:
                return monacoModule.MarkerSeverity.Error
            default:
                return monacoModule.MarkerSeverity.Error
        }
    }

    // Register hover and signature help providers once globally
    if (!celProvidersRegistered) {
        celProvidersRegistered = true
        monacoModule.languages.registerHoverProvider(celLanguage.id, makeCELHoverProvider(schemaByModelUri))
        monacoModule.languages.registerSignatureHelpProvider(celLanguage.id, makeCELSignatureHelpProvider(schemaByModelUri))
    }

    return ({
        options,
        value = '',
        defaultValue = '',
        language = 'cel',
        enableFetchValidation = true,
        validationEnv = EditorValidationEnv.POLICY,
        workflowContext,
        formFieldContext,
        showVariableBrowser = true,
        copilotActive = false,
        onCopilotToggle,
        onChange: propsOnChange,
        ...props
    }: EditorProps): React.ReactElement => {
        const intl = useIntl()
        const isCelEditor = language === 'cel'
        const ref = useRef<monaco.editor.IStandaloneCodeEditor>(null)
        const modelUriRef = useRef<string | null>(null)

        // Variable browser popover state
        const [variableBrowserAnchor, setVariableBrowserAnchor] = useState<HTMLButtonElement | null>(null)

        // Only fetch CEL schema when NOT in read-only mode (performance optimization)
        const shouldFetchSchema = !options?.readOnly && showVariableBrowser
        const celSchema = useCELSchema(
            shouldFetchSchema ? validationEnv : EditorValidationEnv.UNSPECIFIED,
            shouldFetchSchema ? workflowContext : undefined,
            shouldFetchSchema ? formFieldContext : undefined,
        )
        const appSearchClient = useClientRPC<AppSearchClient>(AppSearchClient)
        const userSearchClient = useClientRPC<UserSearchClient>(UserSearchClient)

        // Use controlled text state with deferred value for validation debouncing
        const [text, setText] = useState(value)
        const deferredText = useDeferredValue(text)
        const [contentHeight, setContentHeight] = useState<number | null>(null)

        // Sync text state when value prop changes (prevent stale state)
        useEffect(() => {
            setText(value)
        }, [value])

        // Merge options early to use for validation check
        const mergedOptions = useMemo<EditorOptions>(() => ({
            ...defaultEditorOptions,
            ...options,
        }), [options])

        // Lazy load YAML schema only when YAML language is used
        useEffect(() => {
            if (language === 'yaml') {
                configureYamlSchemaOnce()
            }
        }, [language])

        // Define separate validation functions for each language
        // Skip validation in read-only mode for performance
        const shouldValidate = enableFetchValidation && deferredText.length > 0 && !mergedOptions.readOnly

        const celRequest = CelEditorValidateRequest.create({
            expression: deferredText,
            validationEnv,
            formFieldContext,
            workflowContext,
        })
        const celResp = editorService.useValidateCEL(celRequest, {
            actionable: shouldValidate && language === 'cel',
        })

        const yamlRequest = ConnectorEditorValidateRequest.create({ text: deferredText })
        const yamlResp = validateHTTPConnectorConfigApi.useValidateHTTPConnectorConfig(yamlRequest, {
            actionable: shouldValidate && language === 'yaml',
        })

        const { data, isError, error } = useMemo(() => (language === 'yaml' ? yamlResp : celResp), [language, yamlResp, celResp])

        // Set markers when validation response arrives
        useEffect(() => {
            if (!data || !ref.current) {
                return
            }
            if (error && isError) {
                return
            }
            const model = ref.current.getModel()
            if (!model) {
                return
            }
            monacoModule.editor.setModelMarkers(model, 'owner', data.markers.map((m) => ({
                startLineNumber: m.startLineNumber,
                startColumn: m.startColumn,
                endLineNumber: m.endLineNumber,
                endColumn: m.endColumn,
                message: m.message,
                severity: toSeverity(m.severity),
            })))
        }, [data, isError, error])

        // Register entity completion provider (for app/user search within function params)
        useEffect(() => {
            if (!appSearchClient || !userSearchClient) {
                return
            }

            // Dispose previous provider if it exists (handles hot reloading)
            celCompletionProviderDisposable?.dispose()

            const completionProvider = makeCELCompletionProvider(schemaByModelUri, appSearchClient, userSearchClient, monacoModule)
            celCompletionProviderDisposable = monacoModule.languages.registerCompletionItemProvider(celLanguage.id, completionProvider)
        }, [appSearchClient, userSearchClient])

        // Sync CEL schema to module-level registry for autocomplete
        useEffect(() => {
            const uri = modelUriRef.current
            if (!uri) return

            // Only register if showVariableBrowser=true AND schema has data
            if (showVariableBrowser && celSchema.variables.length > 0) {
                schemaByModelUri.set(uri, celSchema)
            } else {
                // Remove entry when disabled or empty
                schemaByModelUri.delete(uri)
            }

            // Cleanup on unmount or when deps change
            return (): void => {
                schemaByModelUri.delete(uri)
            }
        }, [celSchema, showVariableBrowser])

        const onMount: OnMount = useCallback((editor) => {
            ref.current = editor
            // Capture model URI for schema registry
            const model = editor.getModel()
            if (model) {
                modelUriRef.current = model.uri.toString()
            }
            // Track content height for auto-sizing
            setContentHeight(editor.getContentHeight())
            editor.onDidContentSizeChange((e) => {
                if (e.contentHeightChanged) {
                    setContentHeight(e.contentHeight)
                }
            })
        }, [])

        const onChange: OnChange = useCallback((v, ev) => {
            setText(v || '')
            propsOnChange?.(v, ev)
        }, [propsOnChange])

        const handleCopilotToggle = useCallback((): void => {
            onCopilotToggle?.(!copilotActive)
        }, [copilotActive, onCopilotToggle])

        const handleVariableBrowserClick = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
            setVariableBrowserAnchor(event.currentTarget)
        }, [])

        const handleVariableBrowserClose = useCallback((): void => {
            setVariableBrowserAnchor(null)
        }, [])

        const handleVariableInsert = useCallback((insertText: string): void => {
            const editor = ref.current
            if (!editor) return

            const position = editor.getPosition()
            if (!position) return

            const model = editor.getModel()
            if (!model) return

            // Parse context to find partial text that should be replaced
            const line = model.getLineContent(position.lineNumber)
            const { startColumn } = parseExpressionContext(line, position.column)

            // Create range that replaces partial text (from start of partial to cursor)
            const range = {
                startLineNumber: position.lineNumber,
                startColumn,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
            }

            // Insert text, replacing any partial text
            editor.executeEdits('variableBrowser', [{
                range,
                text: insertText,
                forceMoveMarkers: true,
            }])

            // Focus the editor after insertion
            editor.focus()
            handleVariableBrowserClose()
        }, [handleVariableBrowserClose])

        return <Box
            sx={{
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 1,
                overflow: 'visible', // Changed from 'hidden' to allow error tooltips to overflow
                backgroundColor: editorBackgroundColor,
                position: 'relative',
            }}
        >
            {/* Integrated toolbar header */}
            {isCelEditor && <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    px: 1,
                    py: 0,
                    borderBottom: '1px solid',
                    borderColor: 'grey.100',
                    backgroundColor: editorBackgroundColor,
                    gap: 0,
                    minHeight: 32,
                    borderTopLeftRadius: C1BorderRadius.regular,
                    borderTopRightRadius: C1BorderRadius.regular,
                }}
            >
                {/* AI assistant - hidden in read-only mode or when onCopilotToggle not provided */}
                {onCopilotToggle && !mergedOptions.readOnly && <HasFeature feature={FeatureFlagIdCopilotCelEditor}>
                    <Tooltip
                        title={
                            <FormattedMessage
                                id="cel_ai_assistant_tooltip"
                                defaultMessage="Generate expression with AI"
                            />
                        }
                    >
                        <IconButton
                            onClick={handleCopilotToggle}
                            size="small"
                            sx={{
                                color: 'brand.main',
                                width: 28,
                                height: 28,
                                '&:hover': { backgroundColor: 'grey.100' },
                            }}
                            aria-label="AI assistant"
                        >
                            <C1Icon icon={Sparkles} sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </HasFeature>}
                {/* Variable browser button - hidden in read-only mode */}
                {showVariableBrowser && !mergedOptions.readOnly && <>
                    <Tooltip
                        title={
                            <FormattedMessage
                                id="cel_browser_button_tooltip"
                                defaultMessage="Browse available variables"
                            />
                        }
                    >
                        <IconButton
                            onClick={handleVariableBrowserClick}
                            size="small"
                            sx={{
                                color: 'grey.600',
                                width: 28,
                                height: 28,
                                '&:hover': { backgroundColor: 'grey.100' },
                            }}
                            aria-label={intl.formatMessage({ id: 'editor.browseVariables', defaultMessage: 'Browse variables' })}
                        >
                            <C1Icon icon={Braces} sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Popover
                        open={Boolean(variableBrowserAnchor)}
                        anchorEl={variableBrowserAnchor}
                        onClose={handleVariableBrowserClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    width: 400,
                                    maxHeight: 500,
                                    boxShadow: 3,
                                },
                            },
                        }}
                        // Keep content mounted to avoid re-render on open
                        keepMounted
                    >
                        <VariableBrowserContent schema={celSchema} onInsert={handleVariableInsert} />
                    </Popover>
                </>}
                {isCelEditor && <CELTooltip />}
            </Box>}

            {/* Editor area */}
            <Box
                sx={sxc([
                    mergedOptions.readOnly, {
                        userSelect: 'none',
                        cursor: 'not-allowed',
                    }], {
                    p: 1,
                    minHeight: props.minHeight,
                    height: contentHeight !== null
                        ? `calc(${contentHeight}px + (${spacing.xs} * 2))`
                        : 'auto',
                    maxHeight: props.maxHeight || '500px',
                    position: 'relative',
                    overflow: 'hidden', // Keep overflow hidden for editor content itself
                    borderRadius: '0 0 4px 4px', // Round bottom corners to match container
                })}
            >
                <Editor
                    height="100%"
                    {...props}
                    defaultValue={defaultValue}
                    onChange={onChange}
                    onMount={onMount}
                    language={language}
                    options={mergedOptions}
                    value={value}
                    theme="c1"
                />
            </Box>
        </Box>
    }
}, { ssr: false, loading: Loading })
